/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react'

import { ChainId, isTestnet } from '@src/configs/chainInfo'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { Address } from '@src/types/token'
import { validateDestinationAddress } from '@src/utils/addressUtils'
import { isCrossChainEVMNonEVM, isOriginEVM, isOriginSolana, isOriginTron } from '@src/utils/chainUtils'
import { isFeatureEnabled } from '@src/utils/environment'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'

/**
 * useExchangeDerivedState
 *
 * This hook centralizes various derived states and memoized values
 * that are frequently used in the Exchange component. By offloading
 * these to a dedicated hook, we can keep the ExchangePage.tsx component
 * more maintainable and focused on the primary swap flow.
 */
export function useExchangeDerivedState() {
    // Get wallet connection states
    const {
        isTronConnected,
        tronWalletAddress,
        isSolanaConnected,
        solanaWalletAddress,
        isEVMConnected,
        EVMChainId,
        evmWalletAddress,
    } = useWalletConnections()

    // Get exchange context data
    const { sellData, buyData, mainWalletAddress, quoteResponse, isQuoteLoading } = useExchangeDataContext()

    const { disabledFromChains, disabledToChains } = useMemo(() => {
        const disabledFromChains: number[] = []
        const disabledToChains: number[] = []

        // Check if Celo chain is disabled via feature flag
        const isCeloEnabled = isFeatureEnabled('ENABLE_CELO_CHAIN')
        if (!isCeloEnabled) {
            // Add Celo chain ID to both disabled arrays
            disabledFromChains.push(ChainId.Celo)
            disabledToChains.push(ChainId.Celo)
        }

        const isMantleEnabled = isFeatureEnabled('ENABLE_MANTLE_CHAIN')
        if (!isMantleEnabled) {
            // Add Mantle chain ID to both disabled arrays
            disabledFromChains.push(ChainId.Mantle)
            disabledToChains.push(ChainId.Mantle)
        }

        return { disabledFromChains, disabledToChains }
    }, [sellData.chainId, buyData.chainId])

    /**
     * A helper to check if the user is on the correct wallet connection
     * for the chosen sell chain. This helps us determine if we should
     * display a "Connect Wallet" button or a "Swap" button.
     */
    const isCorrectWalletConnected = useMemo(() => {
        return (
            (isOriginSolana(sellData.chainId) && isSolanaConnected) ||
            (isOriginTron(sellData.chainId) && isTronConnected) ||
            (isOriginEVM(sellData.chainId) && isEVMConnected)
        )
    }, [sellData.chainId, isSolanaConnected, isTronConnected, isEVMConnected])

    /**
     * Check if we are only wrapping or unwrapping native tokens. This
     * is a special case where the swap flow is simpler and only involves
     * deposit or withdraw operations for WETH / wrapped gas tokens.
     */
    const isWrapOrUnwrapOnly = useMemo(() => {
        return isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)
    }, [sellData, buyData])

    /**
     * Check if the destination chain is different from the origin chain,
     * specifically if it is a cross-chain scenario between EVM and non-EVM.
     * This is often relevant for special bridging flows like CCTP or Wormhole.
     */
    const isCrossChain = useMemo(() => {
        return isCrossChainEVMNonEVM(sellData.chainId, buyData.chainId)
    }, [sellData.chainId, buyData.chainId])

    /**
     * Memoize a shortened version of the main EVM address for display or debugging.
     */
    const shortenedAddress = useMemo(() => {
        if (!evmWalletAddress || evmWalletAddress.length < 10) {
            return evmWalletAddress
        }
        // Example: 0x1234...abcd
        return evmWalletAddress.slice(0, 6) + '...' + evmWalletAddress.slice(-4)
    }, [evmWalletAddress])

    /**
     * Validate the user-defined destination address if the user toggles
     * "Send to different wallet" or in cross-chain scenarios.
     */
    function validateAddress(address: Address | undefined, chainId: number) {
        return validateDestinationAddress(address, chainId)
    }

    /**
     * If the user is bridging from EVM to Solana or Tron (or vice versa),
     * we might need to show an input box for the destination address
     * or automatically fill it in with the user's known connected wallet.
     */
    const getAutoDetectedAddress = (forChainId: number): string => {
        if (forChainId === ChainId.Solana && solanaWalletAddress) {
            return solanaWalletAddress.toString()
        }
        if (forChainId === ChainId.Tron && tronWalletAddress) {
            return tronWalletAddress
        }
        if (isOriginEVM(forChainId) && evmWalletAddress) {
            return evmWalletAddress
        }
        // Default to empty if none connected
        return ''
    }

    return {
        // Wallet connection states
        isTronConnected,
        tronWalletAddress,
        isSolanaConnected,
        solanaWalletAddress,
        isEVMConnected,
        EVMChainId,
        evmWalletAddress,

        disabledFromChains,
        disabledToChains,

        // Exchange data
        sellData,
        buyData,
        mainWalletAddress,
        quoteResponse,
        isQuoteLoading,

        // Derived states
        isCrossChain,
        shortenedAddress,

        // Utility functions
        validateAddress,
        getAutoDetectedAddress,
        isCorrectWalletConnected,
        isWrapOrUnwrapOnly,
    }
}
